import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {KeyboardEventHandler} from 'react';
import {X5LessonSnapshot} from '../../../domain';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {AddPageStore} from './parts/AddPageStore';
import {DeletePageStore} from './parts/DeletePageStore';
import {EditPageStore} from './parts/EditPageStore';
import {MovePageStore} from './parts/MovePageStore';
import {ReorderPagesStore} from './parts/ReorderPagesStore';
import {ActivateSnapshotStore} from './snapshot-parts/ActivateSnapshotStore';
import {DeactivateSnapshotStore} from './snapshot-parts/DeactivateSnapshotStore';
import {DeleteSnapshotStore} from './snapshot-parts/DeleteSnapshotStore';
import {TakeSnapshotStore} from './snapshot-parts/TakeSnapshotStore';
import {UpdateSnapshotStore} from './snapshot-parts/UpdateSnapshotStore';
import {X5LessonLoader} from './X5LessonLoader';

type Mode = 'view' |
  'add-page' | 'edit-page' | 'delete-page' | 'reorder-pages' | 'move-page'
  | 'take-snapshot' | 'update-snapshot'
  | 'activate-snapshot' | 'deactivate-snapshot' | 'delete-snapshot';

export class X5LessonStore {
  readonly loader: X5LessonLoader;

  @observable
  mode: Mode;

  @observable.ref
  addPage: AddPageStore | null;
  @observable.ref
  editPage: EditPageStore | null;
  @observable.ref
  deletePage: DeletePageStore | null;
  @observable.ref
  reorder: ReorderPagesStore | null;
  @observable.ref
  move: MovePageStore | null;

  @observable.ref
  takeSnapshot: TakeSnapshotStore | null;
  @observable.ref
  updateSnapshot: UpdateSnapshotStore | null;
  @observable.ref
  activateSnapshot: ActivateSnapshotStore | null;
  @observable.ref
  deactivateSnapshot: DeactivateSnapshotStore | null;
  @observable.ref
  deleteSnapshot: DeleteSnapshotStore | null;

  private readonly disposers = new Disposers();

  constructor(readonly lessonId: number) {
    makeObservable(this);
    this.mode = 'view';

    this.loader = new X5LessonLoader(lessonId);
    this.disposers.add(this.loader);

    this.addPage = this.editPage = this.deletePage =
      this.reorder = this.move = null;
    this.takeSnapshot = this.updateSnapshot =
      this.activateSnapshot = this.deactivateSnapshot =
        this.deleteSnapshot = null;

    this.setup();
  }

  @action
  private toViewMode = () => {
    this.mode = 'view';
    this.addPage = this.editPage = this.deletePage =
      this.reorder = this.move = null;
    this.takeSnapshot = this.updateSnapshot =
      this.activateSnapshot = this.deactivateSnapshot =
        this.deleteSnapshot = null;
  };

  @computed
  get isSaving(): boolean {
    const mode = this.mode;
    if (mode === 'view')
      return false;
    return (
      this.addPage?.save?.isPending === true ||
      this.editPage?.save?.isPending === true ||
      this.deletePage?.save?.isPending === true ||
      this.reorder?.save?.isPending === true ||
      this.move?.save?.isPending === true ||
      this.takeSnapshot?.save?.isPending === true ||
      this.updateSnapshot?.save?.isPending === true ||
      this.activateSnapshot?.save?.isPending === true ||
      this.deactivateSnapshot?.save?.isPending === true ||
      this.deleteSnapshot?.save?.isPending === true);
  }

  @action
  completeOnEnter: KeyboardEventHandler = (event) => {
    if (event.key !== 'Enter')
      return;
    if (this.mode === 'add-page')
      this.completeAdd();
    if (this.mode === 'edit-page')
      this.completeEditPage();
    if (this.mode === 'take-snapshot')
      this.completeTakeSnapshot();
    if (this.mode === 'update-snapshot')
      this.completeUpdateSnapshot();
  };

  /****************************************************************************/

  @action
  beginAddPage = () => {
    this.mode = 'add-page';
    this.addPage = new AddPageStore(this.lessonId);
  };

  @action
  completeAdd = () => {
    this.addPage?.complete();
  };

  /****************************************************************************/

  @action
  beginEditPage(pageId: number) {
    this.mode = 'edit-page';
    let page = this.loader.pageById[pageId]!;
    this.editPage = new EditPageStore(page);
  };

  @action
  completeEditPage = () => {
    this.editPage?.complete();
  };

  /****************************************************************************/

  @action
  beginMovePage = (pageId: number) => {
    this.mode = 'move-page';
    const page = this.loader.pageById[pageId];
    this.move = new MovePageStore(page, this.loader.lookup!);
  };

  @action
  completeMovePage = () => {
    this.move?.complete();
  };

  /****************************************************************************/

  @action
  beginDeletePage = (pageId: number) => {
    this.mode = 'delete-page';
    this.deletePage = new DeletePageStore(pageId);
    this.completeDeletePage();
  };

  @action
  completeDeletePage = () => {
    this.deletePage?.complete();
  };

  /****************************************************************************/

  @action
  beginReorderPages = () => {
    this.mode = 'reorder-pages';
    const pageIds = this.loader.pages.map(x => x.Id);
    this.reorder = new ReorderPagesStore(this.lessonId, pageIds);
  };

  @action
  reorderUp = (index: number) => {
    this.reorder?.swap(index, index - 1);
  };

  @action
  reorderDown = (index: number) => {
    this.reorder?.swap(index, index + 1);
  };

  @action
  completeReorderPages = () => {
    this.reorder?.complete();
  };

  /****************************************************************************/

  @action
  beginTakeSnapshot = () => {
    this.mode = 'take-snapshot';
    this.takeSnapshot = new TakeSnapshotStore(this.lessonId);
  };

  @action
  completeTakeSnapshot = () => {
    this.takeSnapshot?.complete();
  };

  /****************************************************************************/

  @action
  beginUpdateSnapshot(lessonSnapshot: X5LessonSnapshot) {
    this.mode = 'update-snapshot';
    this.updateSnapshot = new UpdateSnapshotStore(lessonSnapshot);
  }

  @action
  completeUpdateSnapshot = () => {
    this.updateSnapshot?.complete();
  };

  /****************************************************************************/

  @action
  beginActivateSnapshot(lessonSnapshot: X5LessonSnapshot) {
    this.mode = 'activate-snapshot';
    this.activateSnapshot = new ActivateSnapshotStore(lessonSnapshot);
    this.completeActivateSnapshot();
  }

  @action
  completeActivateSnapshot = () => {
    this.activateSnapshot?.complete();
  };

  /****************************************************************************/

  @action
  beginDeactivateSnapshot(lessonSnapshot: X5LessonSnapshot) {
    this.mode = 'deactivate-snapshot';
    this.deactivateSnapshot = new DeactivateSnapshotStore(lessonSnapshot);
    this.completeDeactivateSnapshot();
  }

  @action
  completeDeactivateSnapshot = () => {
    this.deactivateSnapshot?.complete();
  };

  /****************************************************************************/

  @action
  beginDeleteSnapshot(snapshotId: number) {
    this.mode = 'delete-snapshot';
    this.deleteSnapshot = new DeleteSnapshotStore(snapshotId);
    this.completeDeleteSnapshot();
  };

  @action
  completeDeleteSnapshot = () => {
    this.deleteSnapshot?.complete();
  };

  /****************************************************************************/

  @action
  cancel = () => {
    if (this.isSaving)
      return;
    this.toViewMode();
  };

  private setup() {
    this.disposers.add(reaction(
      () => [
        this.addPage?.save?.value,
        this.editPage?.save?.value,
        this.deletePage?.save?.value,
        this.reorder?.save?.value,
        this.move?.save?.value,
        this.takeSnapshot?.save?.value,
        this.updateSnapshot?.save?.value,
        this.activateSnapshot?.save?.value,
        this.deactivateSnapshot?.save?.value,
        this.deleteSnapshot?.save?.value
      ],
      this.reloadOnSuccessResponse
    ));
  }

  @action
  private reloadOnSuccessResponse = () => {
    for (let state of [
      this.addPage?.save,
      this.editPage?.save,
      this.deletePage?.save,
      this.reorder?.save,
      this.move?.save,
      this.takeSnapshot?.save,
      this.updateSnapshot?.save,
      this.activateSnapshot?.save,
      this.deactivateSnapshot?.save,
      this.deleteSnapshot?.save
    ])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.loader.loadLesson();
        this.toViewMode();
      }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
